import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Calendar, TrendingUp, Upload, Download, Lock, Eye, Globe, ExternalLink, CheckCircle2, Circle, ClipboardCheck, ChevronDown, Building2, Plus, X, Edit2, Trash2, ClipboardList, Clock, AlertTriangle, Shield } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import DailyBriefingBar from '../components/DailyBriefingBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_CATEGORIES = [
  { value: 'accounting', label: 'Accounting' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'suppliers', label: 'Suppliers' },
  { value: 'hmrc', label: 'HMRC' },
  { value: 'companies_house', label: 'Companies House' },
  { value: 'staff_training_hr', label: 'Staff Training / HR' },
  { value: 'branding_pr', label: 'Branding Assets / PR' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' }
];

const CATEGORY_LABELS = Object.fromEntries(DOCUMENT_CATEGORIES.map(c => [c.value, c.label]));

const PRIORITY_STYLES = {
  high: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', label: 'High' },
  medium: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', label: 'Medium' },
  low: { bg: '#F0FDF4', border: '#BBF7D0', text: '#166534', label: 'Low' }
};

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [financialData, setFinancialData] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', date: '', doc_type: 'hmrc' });
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [trademarks, setTrademarks] = useState([]);
  const [showTmForm, setShowTmForm] = useState(false);
  const [tmForm, setTmForm] = useState({ name: '', status: 'not_started', notes: '' });
  const [editingTmId, setEditingTmId] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', due_date: '', priority: 'medium',
    status: 'open', category: 'task', assigned_to: ''
  });

  const isOwner = user?.role === 'owner';
  const canUploadDocs = isOwner || user?.role === 'accountant';
  const canViewDocs = user?.role !== 'investor';
  const canDownloadDocs = isOwner;

  useEffect(() => {
    fetchCompanyData();
    fetchAllCompanies();
  }, [id]);

  const fetchAllCompanies = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/companies`, { withCredentials: true });
      setCompanies(res.data);
    } catch {}
  };

  const fetchCompanyData = async () => {
    try {
      const [companyRes, deadlinesRes, checklistRes, tasksRes] = await Promise.all([
        axios.get(`${API_URL}/api/companies/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/deadlines?company_id=${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/companies/${id}/checklist`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tasks?company_id=${id}`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);

      setCompany(companyRes.data);
      setDeadlines(deadlinesRes.data);
      setChecklist(checklistRes.data);
      setTasks(tasksRes.data);
      
      // Fetch trademarks
      try {
        const tmRes = await axios.get(`${API_URL}/api/trademarks?company_id=${id}`, { withCredentials: true });
        setTrademarks(tmRes.data);
      } catch { setTrademarks([]); }

      if (canViewDocs) {
        const docsRes = await axios.get(`${API_URL}/api/documents?company_id=${id}`, { withCredentials: true });
        setDocuments(docsRes.data);
      }

      try {
        const financialRes = await axios.get(`${API_URL}/api/transactions?company_id=${id}`, { withCredentials: true });
        setFinancialData(financialRes.data);
      } catch {
        setFinancialData([]);
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleChecklistItem = async (itemId, currentCompleted) => {
    try {
      await axios.put(
        `${API_URL}/api/companies/${id}/checklist/${itemId}`,
        { completed: !currentCompleted },
        { withCredentials: true }
      );
      setChecklist(prev =>
        prev.map(item =>
          item.id === itemId
            ? { ...item, completed: !currentCompleted, completed_at: !currentCompleted ? new Date().toISOString() : null }
            : item
        )
      );
    } catch (error) {
      console.error('Error updating checklist:', error);
    }
  };

  const logDocumentView = async (docId) => {
    try {
      await axios.post(`${API_URL}/api/documents/${docId}/view-log`, {}, { withCredentials: true });
    } catch {}
  };

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0 || !uploadForm.title || !uploadForm.date) {
      alert('Please fill in title and date before uploading');
      return;
    }
    setUploading(true);
    const file = acceptedFiles[0];
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(
        `${API_URL}/api/documents/upload?company_id=${id}&title=${encodeURIComponent(uploadForm.title)}&date=${uploadForm.date}&doc_type=${uploadForm.doc_type}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploadForm({ title: '', date: '', doc_type: 'hmrc' });
      fetchCompanyData();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1
  });

  const downloadDocument = async (docId, filename) => {
    if (!canDownloadDocs) return;
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}/download`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  // Task CRUD
  const TASK_CATEGORIES = [
    { id: 'task', label: 'Task / To-Do' },
    { id: 'meeting', label: 'Meeting / Appointment' },
    { id: 'note', label: 'Note / Memo' },
    { id: 'reminder', label: 'Reminder / Follow-up' },
    { id: 'action', label: 'Action Required' },
  ];

  const TASK_PRIORITIES = {
    high: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', label: 'High' },
    medium: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', label: 'Medium' },
    low: { bg: '#F0FDF4', border: '#BBF7D0', text: '#166534', label: 'Low' },
  };

  const TASK_STATUSES = {
    open: { color: '#D4AF37', bg: '#FFFBEB', label: 'Open' },
    in_progress: { color: '#1E3A8A', bg: '#DBEAFE', label: 'In Progress' },
    done: { color: '#166534', bg: '#DCFCE7', label: 'Done' },
  };

  const resetTaskForm = () => {
    setTaskForm({ title: '', description: '', due_date: '', priority: 'medium', status: 'open', category: 'task', assigned_to: '' });
    setEditingTaskId(null);
    setShowTaskForm(false);
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTaskId) {
        await axios.put(`${API_URL}/api/tasks/${editingTaskId}`, taskForm, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/tasks`, { ...taskForm, company_id: id }, { withCredentials: true });
      }
      resetTaskForm();
      fetchCompanyData();
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleEditTask = (task) => {
    setTaskForm({
      title: task.title, description: task.description || '', due_date: task.due_date || '',
      priority: task.priority, status: task.status, category: task.category, assigned_to: task.assigned_to || ''
    });
    setEditingTaskId(task.id);
    setShowTaskForm(true);
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API_URL}/api/tasks/${taskId}`, { withCredentials: true });
      fetchCompanyData();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${taskId}`, { status: newStatus }, { withCredentials: true });
      fetchCompanyData();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const openTasks = tasks.filter(t => t.status !== 'done');
  const doneTasks = tasks.filter(t => t.status === 'done');

  // Trademark CRUD
  const TM_STATUSES = [
    { value: 'not_started', label: 'Not Started', bg: '#F3F4F6', text: '#374151' },
    { value: 'researching', label: 'Researching', bg: '#DBEAFE', text: '#1E3A8A' },
    { value: 'filed', label: 'Filed', bg: '#FEF3C7', text: '#92400E' },
    { value: 'registered', label: 'Registered', bg: '#D1FAE5', text: '#065F46' },
    { value: 'renewal_due', label: 'Renewal Due', bg: '#FEE2E2', text: '#991B1B' },
  ];

  const handleTmSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTmId) {
        await axios.put(`${API_URL}/api/trademarks/${editingTmId}`, tmForm, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/trademarks`, { ...tmForm, company_id: id }, { withCredentials: true });
      }
      setShowTmForm(false);
      setEditingTmId(null);
      setTmForm({ name: '', status: 'not_started', notes: '' });
      fetchCompanyData();
    } catch (error) {
      console.error('Error saving trademark:', error);
    }
  };

  const handleTmStatusChange = async (tmId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/trademarks/${tmId}`, { status: newStatus }, { withCredentials: true });
      fetchCompanyData();
    } catch (error) {
      console.error('Error updating trademark:', error);
    }
  };

  const handleDeleteTm = async (tmId) => {
    if (!window.confirm('Delete this trademark?')) return;
    try {
      await axios.delete(`${API_URL}/api/trademarks/${tmId}`, { withCredentials: true });
      fetchCompanyData();
    } catch (error) {
      console.error('Error deleting trademark:', error);
    }
  };

  // Checklist stats
  const checklistTotal = checklist.length;
  const checklistDone = checklist.filter(i => i.completed).length;
  const checklistPercent = checklistTotal > 0 ? Math.round((checklistDone / checklistTotal) * 100) : 0;

  // Group checklist by category
  const checklistByCategory = checklist.reduce((acc, item) => {
    const cat = item.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const otherCompanies = companies.filter(c => c.id !== id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#D4AF37' }}></div>
          <p className="mt-4" style={{ color: '#8B7355' }}>Loading company data...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <p style={{ color: '#8B7355' }}>Company not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="company-detail">

      {/* Header — ultra compact with logo */}
      <header className="bg-white" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 text-xs" style={{ color: '#8B7355' }} data-testid="back-to-dashboard-button">
              <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
            </button>
            {otherCompanies.length > 0 && (
              <div className="relative" data-testid="company-switcher">
                <button onClick={() => setShowSwitcher(!showSwitcher)} className="flex items-center gap-1 px-2 py-1 rounded border text-[10px]" style={{ borderColor: '#E5E1DB', color: '#2C2416' }} data-testid="company-switcher-button">
                  <Building2 className="w-3 h-3" style={{ color: '#D4AF37' }} /> Switch <ChevronDown className={`w-2.5 h-2.5 transition-transform ${showSwitcher ? 'rotate-180' : ''}`} style={{ color: '#8B7355' }} />
                </button>
                {showSwitcher && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border rounded shadow-lg z-50" style={{ borderColor: '#E5E1DB' }} data-testid="company-switcher-dropdown">
                    {otherCompanies.map((c) => (
                      <a key={c.id} href={`/company/${c.id}`} className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 no-underline" style={{ borderBottom: '1px solid #F5F1EB', color: '#2C2416' }} onClick={() => setShowSwitcher(false)} data-testid={`switch-to-${c.id}`}>
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} /> {c.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2.5 mt-1.5">
            {(() => {
              const LOGOS = { 'Spaza Global': '/spaza-global-logo.png', 'Spaza Luxe Escapes': '/spaza-luxe-escapes-logo.png', 'Star Global Remit': '/sgr-logo.png', 'Star Talent Group': '/stg-logo.jpg', 'KiddiPlay': '/kiddiplay-logo.jpg', 'Maluti Holdings': '/maluti-logo-web.png', 'GrabbyBag': '/grabbybag-logo.png' };
              const LOGO_BG = { 'Spaza Global': '#0A0A0A', 'Spaza Luxe Escapes': '#FBF8F3', 'Star Global Remit': '#FAFAFA', 'Star Talent Group': '#1A2F4A', 'KiddiPlay': '#FFF5FB', 'Maluti Holdings': '#FFFFFF', 'GrabbyBag': '#FFFFFF' };
              return (
                <div className="w-9 h-9 rounded-full overflow-hidden border-2 flex-shrink-0" style={{ borderColor: company.color, backgroundColor: LOGO_BG[company.name] || '#FAF7F2' }}>
                  {LOGOS[company.name] ? <img src={LOGOS[company.name]} alt={company.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-4 h-4" style={{ color: company.color }} /></div>}
                </div>
              );
            })()}
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-medium tracking-tight truncate" style={{ color: '#2C2416' }}>{company.name}</h1>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: '#8B7355' }}>{company.type}</p>
            </div>
            <div className="text-right flex-shrink-0" data-testid="checklist-progress-badge">
              <span className="text-sm font-medium" style={{ color: checklistPercent === 100 ? '#16A34A' : '#D4AF37' }}>{checklistPercent}%</span>
              <p className="text-[8px] uppercase tracking-widest" style={{ color: '#8B7355' }}>Docs</p>
            </div>
          </div>
          {company.divisions?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5" style={{ borderTop: '1px solid #F0EBE3' }}>
              {company.divisions.map((div, idx) => {
                const divWebsite = company.division_websites?.[div];
                return (
                  <a key={idx} href={divWebsite || '#'} target={divWebsite ? '_blank' : '_self'} rel="noopener noreferrer" className="text-[9px] px-2 py-0.5 rounded-full no-underline hover:shadow-sm flex items-center gap-0.5" style={{ backgroundColor: company.color + '10', color: company.color, border: `1px solid ${company.color}20` }} onClick={divWebsite ? undefined : (e) => e.preventDefault()}>
                    {divWebsite && <Globe className="w-2 h-2" />} {div} {divWebsite && <ExternalLink className="w-2 h-2" />}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">
              Tasks
              {openTasks.length > 0 && (
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                  {openTasks.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="checklist" data-testid="tab-checklist">
              Checklist
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: checklistPercent === 100 ? '#DCFCE7' : '#FEF3C7', color: checklistPercent === 100 ? '#166534' : '#92400E' }}>
                {checklistDone}/{checklistTotal}
              </span>
            </TabsTrigger>
            <TabsTrigger value="deadlines" data-testid="tab-deadlines">Tax & Deadlines</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Timeline & Documents</TabsTrigger>
            <TabsTrigger value="financial" data-testid="tab-financial">P&L / Finance</TabsTrigger>
            <TabsTrigger value="trademarks" data-testid="tab-trademarks">
              <span className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" />
                IP & Trademarks
                {trademarks.length > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#DBEAFE', color: '#1E3A8A' }}>{trademarks.length}</span>
                )}
              </span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" data-testid="overview-content">
            <div className="space-y-4">
              <div className="bg-white border rounded-sm p-4" style={{ borderColor: '#E5E1DB' }}>
                <h2 className="text-sm font-medium uppercase tracking-wider mb-3" style={{ color: '#8B7355' }}>Company Details</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    ['UTR Number', company.utr],
                    ['VAT Number', company.vat],
                    ['Filing Date', company.filing_date ? new Date(company.filing_date).toLocaleDateString('en-GB') : null],
                    ['CT600 Date', company.ct600_date ? new Date(company.ct600_date).toLocaleDateString('en-GB') : null],
                    ['Confirmation Date', company.confirmation_date ? new Date(company.confirmation_date).toLocaleDateString('en-GB') : null],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <label className="text-[9px] uppercase tracking-widest block mb-0.5" style={{ color: '#8B7355' }}>{label}</label>
                      <p className="font-mono text-sm" style={{ color: value ? '#2C2416' : '#CBD5E1' }}>{value || 'Not set'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divisions Section */}
              {company.divisions && company.divisions.length > 0 && (
                <div className="bg-white border rounded-sm p-4" style={{ borderColor: '#E5E1DB' }} data-testid="divisions-section">
                  <h2 className="text-sm font-medium uppercase tracking-wider mb-3" style={{ color: '#8B7355' }}>Divisions & Brands</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div className="p-3 border-2 rounded-sm" style={{ borderColor: company.color, backgroundColor: company.color + '05' }} data-testid="division-parent">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: company.color + '20' }}>
                          <Building2 className="w-3 h-3" style={{ color: company.color }} />
                        </div>
                        <div>
                          <h3 className="font-medium text-xs" style={{ color: '#2C2416' }}>{company.name}</h3>
                          <span className="text-[9px]" style={{ color: company.color }}>Parent</span>
                        </div>
                      </div>
                    </div>
                    {company.divisions.map((div, idx) => {
                      const divWebsite = company.division_websites?.[div];
                      return (
                        <a key={idx} href={divWebsite || '#'} target={divWebsite ? '_blank' : '_self'} rel="noopener noreferrer" className="p-3 border rounded-sm no-underline transition-all hover:shadow-sm group" style={{ borderColor: '#E5E1DB', borderLeftWidth: '3px', borderLeftColor: company.color }} onClick={divWebsite ? undefined : (e) => e.preventDefault()} data-testid={`division-${idx}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: company.color + '10' }}>
                              <Globe className="w-3 h-3" style={{ color: company.color }} />
                            </div>
                            <div>
                              <h3 className="font-medium text-xs" style={{ color: '#2C2416' }}>{div}</h3>
                              {divWebsite && <span className="text-[9px] flex items-center gap-0.5 group-hover:underline" style={{ color: company.color }}>Visit <ExternalLink className="w-2 h-2" /></span>}
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Website */}
              {company.website && (
                <div className="bg-white border rounded-sm shadow-sm p-6 flex items-center justify-between" style={{ borderColor: '#E5E1DB' }}>
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5" style={{ color: '#D4AF37' }} />
                    <div>
                      <p className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>Website</p>
                      <a href={company.website} target="_blank" rel="noopener noreferrer" className="font-medium text-sm no-underline hover:underline" style={{ color: '#2C2416' }}>{company.website}</a>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4" style={{ color: '#CBD5E1' }} />
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" data-testid="tasks-content">
            <div className="space-y-6">
              {/* Add Task Button */}
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-light" style={{ color: '#2C2416' }}>Tasks & Action Items</h2>
                <button
                  onClick={() => { resetTaskForm(); setShowTaskForm(true); }}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-sm transition-all duration-200 hover:shadow-md"
                  style={{ backgroundColor: company.color, color: 'white' }}
                  data-testid="add-task-button"
                >
                  <Plus className="w-4 h-4" /><span>Add Task</span>
                </button>
              </div>

              {/* Task Form */}
              {showTaskForm && (
                <div className="bg-white border rounded-sm shadow-sm p-6" style={{ borderColor: '#E5E1DB' }} data-testid="task-form">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium" style={{ color: '#2C2416' }}>{editingTaskId ? 'Edit Task' : 'New Task'}</h3>
                    <button onClick={resetTaskForm} className="p-1"><X className="w-5 h-5" style={{ color: '#8B7355' }} /></button>
                  </div>
                  <form onSubmit={handleTaskSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Title</label>
                        <input type="text" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} className="w-full px-3 py-2.5 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} placeholder="What needs doing?" required data-testid="task-title-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Category</label>
                        <select value={taskForm.category} onChange={(e) => setTaskForm({ ...taskForm, category: e.target.value })} className="w-full px-3 py-2.5 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} data-testid="task-category-select">
                          {TASK_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Due Date</label>
                        <input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} className="w-full px-3 py-2.5 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} data-testid="task-date-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Priority</label>
                        <select value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })} className="w-full px-3 py-2.5 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} data-testid="task-priority-select">
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Assigned To</label>
                        <input type="text" value={taskForm.assigned_to} onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })} className="w-full px-3 py-2.5 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} placeholder="Name of person" data-testid="task-assigned-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Status</label>
                        <select value={taskForm.status} onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })} className="w-full px-3 py-2.5 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} data-testid="task-status-select">
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="done">Done</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: '#2C2416' }}>Description / Notes</label>
                      <textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} rows={3} className="w-full px-3 py-2.5 border rounded-sm focus:outline-none" style={{ borderColor: '#E5E1DB' }} placeholder="Details, context, notes..." data-testid="task-description-input" />
                    </div>
                    <div className="flex gap-3">
                      <button type="submit" className="px-5 py-2.5 rounded-sm transition-all hover:shadow-md" style={{ backgroundColor: company.color, color: 'white' }} data-testid="task-submit-button">{editingTaskId ? 'Update' : 'Add Task'}</button>
                      <button type="button" onClick={resetTaskForm} className="px-5 py-2.5 bg-white border rounded-sm" style={{ borderColor: '#E5E1DB' }}>Cancel</button>
                    </div>
                  </form>
                </div>
              )}

              {/* Open Tasks */}
              {openTasks.length === 0 && doneTasks.length === 0 ? (
                <div className="bg-white border rounded-sm shadow-sm p-12 text-center" style={{ borderColor: '#E5E1DB' }} data-testid="no-tasks">
                  <ClipboardList className="w-16 h-16 mx-auto mb-4" style={{ color: '#E5E1DB' }} />
                  <h3 className="text-xl font-light mb-2" style={{ color: '#2C2416' }}>No tasks yet</h3>
                  <p style={{ color: '#8B7355' }}>Add your first task, meeting, or note for {company.name}.</p>
                </div>
              ) : (
                <>
                  {openTasks.length > 0 && (
                    <div className="space-y-2" data-testid="open-tasks">
                      {openTasks.map(task => {
                        const priority = TASK_PRIORITIES[task.priority] || TASK_PRIORITIES.medium;
                        const status = TASK_STATUSES[task.status] || TASK_STATUSES.open;
                        const catLabel = TASK_CATEGORIES.find(c => c.id === task.category)?.label || task.category;
                        const isOverdue = task.due_date && task.status !== 'done' && new Date(task.due_date) < new Date();
                        return (
                          <div key={task.id} className="bg-white border rounded-sm p-4 transition-all hover:shadow-md" style={{ borderColor: '#E5E1DB', borderLeftWidth: '4px', borderLeftColor: priority.text }} data-testid={`task-${task.id}`}>
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => handleTaskStatusChange(task.id, task.status === 'open' ? 'in_progress' : 'done')}
                                className="mt-0.5 flex-shrink-0 transition-all"
                                data-testid={`task-toggle-${task.id}`}
                              >
                                {task.status === 'in_progress' ? (
                                  <Clock className="w-5 h-5" style={{ color: '#1E3A8A' }} />
                                ) : (
                                  <Circle className="w-5 h-5" style={{ color: '#CBD5E1' }} />
                                )}
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <h4 className="font-medium" style={{ color: '#2C2416' }}>{task.title}</h4>
                                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: status.bg, color: status.color }}>{status.label}</span>
                                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: priority.bg, color: priority.text, border: `1px solid ${priority.border}` }}>{priority.label}</span>
                                  <span className="text-xs px-2 py-0.5 rounded-full border" style={{ borderColor: '#E5E1DB', color: '#8B7355' }}>{catLabel}</span>
                                  {isOverdue && <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }}>Overdue</span>}
                                </div>
                                {task.description && <p className="text-sm mb-1" style={{ color: '#8B7355' }}>{task.description}</p>}
                                <div className="flex items-center gap-4 text-xs flex-wrap" style={{ color: '#8B7355' }}>
                                  {task.due_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {new Date(task.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                    </span>
                                  )}
                                  {task.assigned_to && <span>Assigned: <strong>{task.assigned_to}</strong></span>}
                                  <span>Added by {task.created_by_name || 'Unknown'} on {new Date(task.created_at).toLocaleDateString('en-GB')}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => handleEditTask(task)} className="p-1.5 rounded-sm transition-all hover:bg-gray-50" data-testid={`task-edit-${task.id}`}>
                                  <Edit2 className="w-3.5 h-3.5" style={{ color: '#8B7355' }} />
                                </button>
                                <button onClick={() => handleDeleteTask(task.id)} className="p-1.5 rounded-sm transition-all hover:bg-red-50" data-testid={`task-delete-${task.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Done Tasks */}
                  {doneTasks.length > 0 && (
                    <div data-testid="done-tasks">
                      <h3 className="text-sm uppercase tracking-widest mb-3 flex items-center gap-2" style={{ color: '#8B7355' }}>
                        <CheckCircle2 className="w-4 h-4" style={{ color: '#16A34A' }} />
                        Completed ({doneTasks.length})
                      </h3>
                      <div className="space-y-2">
                        {doneTasks.map(task => (
                          <div key={task.id} className="bg-white border rounded-sm p-4 opacity-60" style={{ borderColor: '#E5E1DB' }} data-testid={`task-done-${task.id}`}>
                            <div className="flex items-center gap-3">
                              <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: '#16A34A' }} />
                              <div className="flex-1 min-w-0">
                                <span className="line-through text-sm" style={{ color: '#2C2416' }}>{task.title}</span>
                                <div className="text-xs mt-0.5" style={{ color: '#8B7355' }}>
                                  {task.completed_by && `Completed by ${task.completed_by}`}
                                  {task.completed_at && ` on ${new Date(task.completed_at).toLocaleDateString('en-GB')}`}
                                </div>
                              </div>
                              <button onClick={() => handleDeleteTask(task.id)} className="p-1.5 rounded-sm transition-all hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* Checklist Tab */}
          <TabsContent value="checklist" data-testid="checklist-content">
            <div className="space-y-6">
              {/* Progress Bar */}
              <div className="bg-white border rounded-sm shadow-sm p-6" style={{ borderColor: '#E5E1DB' }} data-testid="checklist-progress">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5" style={{ color: '#D4AF37' }} />
                    <h2 className="text-xl font-light" style={{ color: '#2C2416' }}>Document Readiness</h2>
                  </div>
                  <span className="text-lg font-medium" style={{ color: checklistPercent === 100 ? '#16A34A' : '#D4AF37' }}>
                    {checklistDone} of {checklistTotal} complete
                  </span>
                </div>
                <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#F5F1EB' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${checklistPercent}%`,
                      backgroundColor: checklistPercent === 100 ? '#16A34A' : '#D4AF37'
                    }}
                    data-testid="checklist-progress-bar"
                  />
                </div>
                {checklistPercent === 100 && (
                  <p className="mt-3 text-sm font-medium" style={{ color: '#16A34A' }} data-testid="checklist-complete-message">
                    All documents accounted for! This company's filing is fully up to date.
                  </p>
                )}
              </div>

              {/* Checklist by Category */}
              {Object.entries(checklistByCategory).map(([cat, items]) => {
                const catDone = items.filter(i => i.completed).length;
                const catTotal = items.length;
                return (
                  <div key={cat} className="bg-white border rounded-sm shadow-sm overflow-hidden" style={{ borderColor: '#E5E1DB' }} data-testid={`checklist-category-${cat}`}>
                    <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: '#FDFCFB', borderBottom: '1px solid #E5E1DB' }}>
                      <h3 className="text-sm font-semibold uppercase tracking-widest" style={{ color: '#8B7355' }}>
                        {CATEGORY_LABELS[cat] || cat}
                      </h3>
                      <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ backgroundColor: catDone === catTotal ? '#DCFCE7' : '#FEF3C7', color: catDone === catTotal ? '#166534' : '#92400E' }}>
                        {catDone}/{catTotal}
                      </span>
                    </div>
                    <div className="divide-y" style={{ borderColor: '#F5F1EB' }}>
                      {items.map((item) => {
                        const priority = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.medium;
                        return (
                          <div
                            key={item.id}
                            className="px-6 py-4 flex items-center gap-4 transition-all duration-200 hover:bg-gray-50"
                            data-testid={`checklist-item-${item.id}`}
                          >
                            <button
                              onClick={() => toggleChecklistItem(item.id, item.completed)}
                              className="flex-shrink-0 transition-all duration-200"
                              data-testid={`checklist-toggle-${item.id}`}
                            >
                              {item.completed ? (
                                <CheckCircle2 className="w-6 h-6" style={{ color: '#16A34A' }} />
                              ) : (
                                <Circle className="w-6 h-6" style={{ color: '#CBD5E1' }} />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p
                                className={`font-medium ${item.completed ? 'line-through opacity-50' : ''}`}
                                style={{ color: '#2C2416' }}
                              >
                                {item.item}
                              </p>
                              {item.completed && item.completed_at && (
                                <p className="text-xs mt-0.5" style={{ color: '#16A34A' }}>
                                  Completed {new Date(item.completed_at).toLocaleDateString('en-GB')}
                                </p>
                              )}
                            </div>
                            <span
                              className="text-xs px-2 py-1 rounded-full flex-shrink-0"
                              style={{ backgroundColor: priority.bg, color: priority.text, border: `1px solid ${priority.border}` }}
                            >
                              {priority.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Deadlines Tab */}
          <TabsContent value="deadlines" data-testid="deadlines-content">
            <div className="bg-white border rounded-sm shadow-sm p-8" style={{ borderColor: '#E5E1DB' }}>
              <div className="flex items-center gap-2 mb-6">
                <Calendar className="w-6 h-6" style={{ color: company.color }} />
                <h2 className="text-2xl font-light" style={{ color: '#2C2416' }}>Deadlines & Compliance Dates</h2>
              </div>
              {deadlines.length === 0 ? (
                <p style={{ color: '#8B7355' }}>No deadlines set for this company</p>
              ) : (
                <div className="space-y-4">
                  {deadlines.map((deadline) => {
                    const isPast = new Date(deadline.date) < new Date();
                    const isUrgent = !isPast && Math.ceil((new Date(deadline.date) - new Date()) / (1000 * 60 * 60 * 24)) <= 7;
                    return (
                      <div
                        key={deadline.id}
                        className="p-4 border rounded-sm"
                        style={{ backgroundColor: isPast ? '#FAFAF9' : isUrgent ? '#FEF2F2' : '#FDFCFB', borderColor: isUrgent ? '#FCA5A5' : '#E5E1DB' }}
                        data-testid={`deadline-${deadline.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-lg" style={{ color: '#2C2416' }}>{deadline.title}</h3>
                            <p className="text-sm uppercase tracking-widest mt-1" style={{ color: '#8B7355' }}>{deadline.deadline_type}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-lg" style={{ color: '#2C2416' }}>{new Date(deadline.date).toLocaleDateString('en-GB')}</p>
                            <p className={`text-sm font-medium mt-1 ${deadline.status === 'completed' ? 'text-green-600' : isUrgent ? 'text-red-600' : ''}`}>
                              {deadline.status.toUpperCase()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" data-testid="documents-content">
            {!canViewDocs ? (
              <div className="bg-white border rounded-sm shadow-sm p-8 text-center" style={{ borderColor: '#E5E1DB' }}>
                <Lock className="w-12 h-12 mx-auto mb-4" style={{ color: '#CBD5E1' }} />
                <h3 className="text-xl font-medium mb-2" style={{ color: '#2C2416' }}>Access Restricted</h3>
                <p style={{ color: '#8B7355' }}>Documents are only available to Owners and Staff.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {!canDownloadDocs && (
                  <div className="flex items-center gap-3 p-4 rounded-sm" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }} data-testid="view-only-notice">
                    <Eye className="w-5 h-5 flex-shrink-0" style={{ color: '#92400E' }} />
                    <p className="text-sm" style={{ color: '#92400E' }}>
                      <strong>View Only</strong> — Downloading, printing, and exporting are restricted to the Owner only. All views are logged.
                    </p>
                  </div>
                )}

                {canUploadDocs && (
                  <div className="bg-white border rounded-sm shadow-sm p-8" style={{ borderColor: '#E5E1DB' }}>
                    <h2 className="text-2xl font-light mb-6" style={{ color: '#2C2416' }}>Upload Document</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block" style={{ color: '#2C2416' }}>Title</label>
                        <input type="text" value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} className="w-full px-3 py-2 border rounded-sm focus:outline-none focus:ring-2" style={{ borderColor: '#E5E1DB' }} placeholder="e.g. Annual Return 2025" data-testid="upload-title-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block" style={{ color: '#2C2416' }}>Date</label>
                        <input type="date" value={uploadForm.date} onChange={(e) => setUploadForm({ ...uploadForm, date: e.target.value })} className="w-full px-3 py-2 border rounded-sm focus:outline-none focus:ring-2" style={{ borderColor: '#E5E1DB' }} data-testid="upload-date-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block" style={{ color: '#2C2416' }}>Category</label>
                        <select value={uploadForm.doc_type} onChange={(e) => setUploadForm({ ...uploadForm, doc_type: e.target.value })} className="w-full px-3 py-2 border rounded-sm focus:outline-none focus:ring-2" style={{ borderColor: '#E5E1DB' }} data-testid="upload-type-select">
                          {DOCUMENT_CATEGORIES.map((cat) => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div {...getRootProps()} className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-all duration-200 ${isDragActive ? 'bg-amber-50' : ''}`} style={{ borderColor: isDragActive ? '#D4AF37' : '#E5E1DB' }} data-testid="upload-dropzone">
                      <input {...getInputProps()} />
                      <Upload className="w-12 h-12 mx-auto mb-4" style={{ color: '#CBD5E1' }} />
                      {uploading ? (
                        <p style={{ color: '#8B7355' }}>Uploading...</p>
                      ) : (
                        <>
                          <p className="font-medium mb-2" style={{ color: '#2C2416' }}>Drop PDF here or click to browse</p>
                          <p className="text-sm" style={{ color: '#8B7355' }}>PDF files only, max 10MB</p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-white border rounded-sm shadow-sm p-8" style={{ borderColor: '#E5E1DB' }}>
                  <div className="flex items-center gap-2 mb-6">
                    <FileText className="w-6 h-6" style={{ color: company.color }} />
                    <h2 className="text-2xl font-light" style={{ color: '#2C2416' }}>Document Timeline</h2>
                  </div>
                  {documents.length === 0 ? (
                    <p style={{ color: '#8B7355' }}>No documents uploaded yet</p>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-6 top-0 bottom-0 w-0.5" style={{ backgroundColor: '#E5E1DB' }} />
                      <div className="space-y-6">
                        {documents.map((doc) => (
                          <div key={doc.id} className="relative flex gap-4" data-testid={`document-${doc.id}`}>
                            <div className="w-12 h-12 rounded-full flex items-center justify-center z-10 border-4 border-white" style={{ backgroundColor: company.color }}>
                              <FileText className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 border rounded-sm p-4" style={{ backgroundColor: '#FDFCFB', borderColor: '#E5E1DB' }}>
                              <div className="flex justify-between items-start">
                                <div>
                                  <h3 className="font-medium text-lg" style={{ color: '#2C2416' }}>{doc.title}</h3>
                                  <p className="text-sm uppercase tracking-widest mt-1" style={{ color: '#8B7355' }}>
                                    {CATEGORY_LABELS[doc.doc_type] || doc.doc_type}
                                  </p>
                                  <p className="text-sm mt-2" style={{ color: '#8B7355' }}>{doc.original_filename}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-mono text-sm mb-2" style={{ color: '#2C2416' }}>{new Date(doc.date).toLocaleDateString('en-GB')}</p>
                                  {canDownloadDocs ? (
                                    <button onClick={() => downloadDocument(doc.id, doc.original_filename)} className="flex items-center gap-1 text-sm transition-all duration-200 hover:opacity-80" style={{ color: '#D4AF37' }} data-testid={`download-doc-${doc.id}`}>
                                      <Download className="w-4 h-4" /><span>Download</span>
                                    </button>
                                  ) : (
                                    <button onClick={() => logDocumentView(doc.id)} className="flex items-center gap-1 text-sm" style={{ color: '#8B7355' }} data-testid={`view-doc-${doc.id}`}>
                                      <Eye className="w-4 h-4" /><span>View Only</span>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial" data-testid="financial-content">
            <div className="bg-white border rounded-sm shadow-sm p-8" style={{ borderColor: '#E5E1DB' }}>
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-6 h-6" style={{ color: company.color }} />
                <h2 className="text-2xl font-light" style={{ color: '#2C2416' }}>Financial Overview</h2>
              </div>
              {financialData.length === 0 ? (
                <div>
                  <p className="mb-4" style={{ color: '#8B7355' }}>No financial data available. Connect with Xero to import data.</p>
                  <button className="px-6 py-3 rounded-sm transition-all duration-200 hover:shadow-md" style={{ backgroundColor: '#D4AF37', color: 'white' }} data-testid="connect-xero-button">
                    Connect Xero
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-medium mb-4" style={{ color: '#2C2416' }}>Profit & Loss Trend</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={financialData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E1DB" />
                        <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="revenue" stroke="#16A34A" strokeWidth={2} name="Revenue" />
                        <Line type="monotone" dataKey="expenses" stroke="#DC2626" strokeWidth={2} name="Expenses" />
                        <Line type="monotone" dataKey="profit_loss" stroke={company.color} strokeWidth={3} name="Profit/Loss" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Trademarks & IP Tab */}
          <TabsContent value="trademarks" data-testid="trademarks-content">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-medium" style={{ color: '#2C2416' }}>IP & Trademark Register</h3>
                  <p className="text-sm mt-1" style={{ color: '#8B7355' }}>Track trademark applications, filings, and renewals</p>
                </div>
                {isOwner && (
                  <button
                    onClick={() => { setShowTmForm(true); setEditingTmId(null); setTmForm({ name: '', status: 'not_started', notes: '' }); }}
                    className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all hover:shadow-md"
                    style={{ backgroundColor: company.color, color: 'white' }}
                    data-testid="add-trademark-btn"
                  >
                    <Plus className="w-4 h-4" /> Add Trademark
                  </button>
                )}
              </div>

              {/* Trademark Form Modal */}
              {showTmForm && (
                <div className="bg-white border rounded-lg p-6 shadow-sm" style={{ borderColor: '#E5E1DB' }}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium" style={{ color: '#2C2416' }}>{editingTmId ? 'Edit Trademark' : 'Add Trademark'}</h4>
                    <button onClick={() => { setShowTmForm(false); setEditingTmId(null); }} className="p-1 hover:bg-gray-100 rounded">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <form onSubmit={handleTmSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: '#8B7355' }}>Name / Brand *</label>
                      <input
                        type="text"
                        value={tmForm.name}
                        onChange={(e) => setTmForm({ ...tmForm, name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-sm text-sm focus:outline-none"
                        style={{ borderColor: '#E5E1DB' }}
                        placeholder="e.g. Spaza Global, MAPS"
                        required
                        data-testid="tm-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: '#8B7355' }}>Status</label>
                        <select
                          value={tmForm.status}
                          onChange={(e) => setTmForm({ ...tmForm, status: e.target.value })}
                          className="w-full px-3 py-2 border rounded-sm text-sm focus:outline-none"
                          style={{ borderColor: '#E5E1DB' }}
                          data-testid="tm-status-select"
                        >
                          {TM_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: '#8B7355' }}>Filing Date</label>
                        <input
                          type="date"
                          value={tmForm.filing_date || ''}
                          onChange={(e) => setTmForm({ ...tmForm, filing_date: e.target.value })}
                          className="w-full px-3 py-2 border rounded-sm text-sm focus:outline-none"
                          style={{ borderColor: '#E5E1DB' }}
                          data-testid="tm-filing-date-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: '#8B7355' }}>Registration No.</label>
                        <input
                          type="text"
                          value={tmForm.registration_number || ''}
                          onChange={(e) => setTmForm({ ...tmForm, registration_number: e.target.value })}
                          className="w-full px-3 py-2 border rounded-sm text-sm focus:outline-none"
                          style={{ borderColor: '#E5E1DB' }}
                          placeholder="UK00000000"
                          data-testid="tm-reg-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: '#8B7355' }}>Renewal Date</label>
                        <input
                          type="date"
                          value={tmForm.renewal_date || ''}
                          onChange={(e) => setTmForm({ ...tmForm, renewal_date: e.target.value })}
                          className="w-full px-3 py-2 border rounded-sm text-sm focus:outline-none"
                          style={{ borderColor: '#E5E1DB' }}
                          data-testid="tm-renewal-date-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: '#8B7355' }}>Notes</label>
                      <textarea
                        value={tmForm.notes || ''}
                        onChange={(e) => setTmForm({ ...tmForm, notes: e.target.value })}
                        className="w-full px-3 py-2 border rounded-sm text-sm focus:outline-none"
                        style={{ borderColor: '#E5E1DB' }}
                        rows={2}
                        placeholder="Any notes about the trademark application..."
                        data-testid="tm-notes-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" className="px-4 py-2 rounded-sm text-sm font-medium text-white transition-all hover:opacity-90" style={{ backgroundColor: company.color }} data-testid="tm-save-btn">
                        {editingTmId ? 'Update' : 'Add Trademark'}
                      </button>
                      <button type="button" onClick={() => { setShowTmForm(false); setEditingTmId(null); }} className="px-4 py-2 rounded-sm text-sm border" style={{ borderColor: '#E5E1DB', color: '#8B7355' }}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Trademark List */}
              {trademarks.length === 0 ? (
                <div className="text-center py-12 bg-white border rounded-lg" style={{ borderColor: '#E5E1DB' }}>
                  <Shield className="w-12 h-12 mx-auto mb-4" style={{ color: '#E5E1DB' }} />
                  <p className="text-sm" style={{ color: '#8B7355' }}>No trademarks registered yet</p>
                </div>
              ) : (
                <div className="bg-white border rounded-lg overflow-hidden" style={{ borderColor: '#E5E1DB' }}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ backgroundColor: '#FDFCFB', borderBottom: '2px solid #E5E1DB' }}>
                          <th className="text-left px-5 py-3 font-medium text-xs uppercase tracking-wider" style={{ color: '#8B7355' }}>Name / Brand</th>
                          <th className="text-left px-5 py-3 font-medium text-xs uppercase tracking-wider" style={{ color: '#8B7355' }}>Status</th>
                          <th className="text-left px-5 py-3 font-medium text-xs uppercase tracking-wider hidden sm:table-cell" style={{ color: '#8B7355' }}>Filing Date</th>
                          <th className="text-left px-5 py-3 font-medium text-xs uppercase tracking-wider hidden md:table-cell" style={{ color: '#8B7355' }}>Reg. No.</th>
                          <th className="text-left px-5 py-3 font-medium text-xs uppercase tracking-wider hidden md:table-cell" style={{ color: '#8B7355' }}>Renewal</th>
                          {isOwner && <th className="px-5 py-3"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {trademarks.map((tm) => {
                          const statusStyle = TM_STATUSES.find(s => s.value === tm.status) || TM_STATUSES[0];
                          return (
                            <tr key={tm.id} className="transition-all hover:bg-gray-50" style={{ borderBottom: '1px solid #F5F1EB' }} data-testid={`tm-row-${tm.id}`}>
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-4 h-4 flex-shrink-0" style={{ color: company.color }} />
                                  <div>
                                    <span className="font-medium" style={{ color: '#2C2416' }}>{tm.name}</span>
                                    {tm.notes && <p className="text-xs mt-0.5" style={{ color: '#8B7355' }}>{tm.notes.substring(0, 60)}{tm.notes.length > 60 ? '...' : ''}</p>}
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-3">
                                {isOwner ? (
                                  <select
                                    value={tm.status}
                                    onChange={(e) => handleTmStatusChange(tm.id, e.target.value)}
                                    className="text-xs font-medium px-2 py-1 rounded-full border-0 cursor-pointer"
                                    style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                                    data-testid={`tm-status-${tm.id}`}
                                  >
                                    {TM_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                  </select>
                                ) : (
                                  <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}>
                                    {statusStyle.label}
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3 hidden sm:table-cell" style={{ color: '#8B7355' }}>
                                {tm.filing_date ? new Date(tm.filing_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                              </td>
                              <td className="px-5 py-3 hidden md:table-cell" style={{ color: '#8B7355' }}>
                                {tm.registration_number || '—'}
                              </td>
                              <td className="px-5 py-3 hidden md:table-cell" style={{ color: '#8B7355' }}>
                                {tm.renewal_date ? new Date(tm.renewal_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                              </td>
                              {isOwner && (
                                <td className="px-5 py-3">
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => { setEditingTmId(tm.id); setTmForm({ name: tm.name, status: tm.status, notes: tm.notes || '', filing_date: tm.filing_date || '', registration_number: tm.registration_number || '', renewal_date: tm.renewal_date || '' }); setShowTmForm(true); }}
                                      className="p-1.5 rounded-sm transition-all hover:bg-gray-100"
                                      data-testid={`tm-edit-${tm.id}`}
                                    >
                                      <Edit2 className="w-3.5 h-3.5" style={{ color: '#8B7355' }} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTm(tm.id)}
                                      className="p-1.5 rounded-sm transition-all hover:bg-red-50"
                                      data-testid={`tm-delete-${tm.id}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer with Company Website */}
      {company.website && (
        <footer className="border-t mt-8" style={{ borderColor: '#E5E1DB', backgroundColor: '#FDFCFB' }} data-testid="company-footer">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
            <div className="flex items-center justify-center gap-2">
              <Globe className="w-4 h-4" style={{ color: '#8B7355' }} />
              <a href={company.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm font-medium transition-all duration-200 hover:opacity-80" style={{ color: company.color }} data-testid="company-website-link">
                {company.website.replace('https://', '').replace('http://', '')}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default CompanyDetail;
